import useAppStore from "@/context/store";
import { getExpsValue } from "@/tracker/abTest";
import { useCallback, useEffect, useState } from "react";

interface IABData<T extends Record<string, string | string[]>> {
  /** 实验变量名 */
  variableName: string;
  /** 实验默认值 */
  defaultValue: T[keyof T];
  /** 实验版本映射 */
  map?: T;
}

/**
 * 用于ABtest的hook
 * @param config AB实验配置对象
 * @example
 * useABTest({
 *   variableName: "test",
 *   defaultValue: "default",
 *   map: { planA: "default", planB: "B" }
 * })
 * // 返回 {planA: true, planB: false, loading: true}
 */
export default function useABTest<T extends Record<string, string | string[]>>(
  config: IABData<T>
) {
  const { variableName, defaultValue, map } = config;
  const user = useAppStore((s) => s.user);
  const [value, setValue] = useState(defaultValue as string);
  const [loading, setLoading] = useState(true);

  const queryAction = useCallback(async () => {
    setLoading(true);
    const result = await getExpsValue(variableName, defaultValue);
    setLoading(false);
    setValue(result as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, variableName, user?.user_id]); // 登录用户变化了，可能会导致ab策略变化，所以需要重新获取ab实验结果

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  return Object.entries(map || {}).reduce(
    (result, item) => {
      if (Array.isArray(item[1])) {
        result[item[0]] = (item[1] as string[]).includes(value);
      } else {
        result[item[0]] = value === item[1];
      }
      return result;
    },
    {
      loading,
      value,
    }
  ) as Record<keyof T, boolean> & { loading: boolean; value: string };
}

/**
 * 用于获取多个ABtest实验结果的hook
 * @param experiments AB实验配置数组
 * @example
 * useMultiABTest([
 *   { variableName: "test1", defaultValue: "default", map: {planA: "default", planB: "B"} },
 *   { variableName: "test2", defaultValue: "default", map: {planC: "default", planD: "D"} }
 * ])
 * // 返回 { test1: {planA: true, planB: false}, test2: {planC: true, planD: false}, loading: true }
 */
export function useMultiABTest<T extends Record<string, string | string[]>>(
  experiments: IABData<T>[]
) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const queryAction = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(
      experiments.map(({ variableName, defaultValue }) =>
        getExpsValue(variableName, defaultValue).then((result) => ({
          variableName,
          result,
        }))
      )
    );

    setValues(
      results.reduce(
        (acc, { variableName, result }) => ({
          ...acc,
          [variableName]: result,
        }),
        {}
      )
    );
    setLoading(false);
  }, [experiments]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  return {
    ...experiments.reduce((acc, { variableName, map }) => {
      if (!map) return acc;

      acc[variableName] = Object.entries(map).reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: values[variableName] === value,
        }),
        {}
      );
      return acc;
    }, {} as Record<string, Record<string, boolean>>),
    loading,
    values,
  };
}

const cache = new Map<string, any>();

/**
 * 带缓存的AB实验hook
 * 为了解决重复获取ab实验时，会有异步更新的过程，导致版本变化
 * 只有第一次调用会有ab实验变化，后面都是直接返回缓存的值
 * @param config AB实验配置对象
 * @returns
 */
export const useABTestWithCache: typeof useABTest = (config) => {
  const key = config.variableName;
  const cached = cache.get(key);

  const result = useABTest(config);

  useEffect(() => {
    if (result.loading) return;

    cache.set(key, result);
  }, [result.loading, result.value]);

  return cached || result;
};
